import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import TileButton, { tileDataProp } from 'components/atoms/TileButton';
import { TPODataContext } from 'contexts/TPOContext';
import { PlanSummarySubTab } from 'types/tradePromoOptimization';
import InfiniteScroller from '../../InfiniteScroller';
// import { CustomTable } from 'components/template/PricingSimulator/PricingSimulatorIndexTableWrapper';
import TableErrorBox from '../../ErrorBox/TableErrorBox';
import { useFetchKpiTableData } from 'hooks/tradePromoOptimization/events/usefetchKpiTableData';
import { tradePromoOptimizationUrls } from 'config/urls';
import { getKpiTableColumns, getPlanSummaryTableColumns } from 'utils/getTradePromoOptimizationTableColumns';
import {
    calculateFontSize,
    compareUsers,
    formatNumberWithCommas,
    getConfig,
    transformFiltersV2,
} from 'utils/helperMethods';
import Footer from '../../PricingSimulator/Footer';
import { errorStatuses, pollingStatuses } from 'components/organism/PricingSimulator/UpdateOrViewPricingSimulator';
import _ from 'lodash';
import { UserContext } from 'contexts/ConfigContext';
import { useFillHeightToFitScreen } from 'hooks/general/useFillHeightToFitScreen';
import dayjs from 'dayjs';
import styled from '@emotion/styled';
import { Table } from 'antd';
import { colorConstants } from 'config/colorConstants';
import tw from 'twin.macro';
import DownloadExcel from 'components/molecules/MicroIntractions/DownloadExcel';
import { planSummaryTableDownload } from 'parser/graphToExcelDownloadParser';

// Plan summary sub-tab options
const summaryTabs: tileDataProp[] = [
    { label: 'Product View', key: 'ps_product_view' },
    { label: 'Tactic View', key: 'ps_tactic_view' },
];

const CustomTable = styled(Table)`
    // .ant-table-sticky-scroll {
    //     position: static;
    // }
    .ant-table-thead > tr > th {
        background: ${colorConstants.grey};
        padding: 6px;
        // height: 68px;
    }
    .ant-table-header col:not(:last-child),
    .ant-table-thead
        > tr
        > th:not(:last-child):not(.ant-table-selection-column):not(.ant-table-row-expand-icon-cell):not(
            [colspan]
        ):before {
        background: none;
    }
    .ant-table-column-sorter {
        position: relative;
        left: -10px;
    }
    .ant-table-column-sorter-down.active,
    .ant-table-column-sorter-up.active {
        ${tw`text-purpleFont`};
    }
    // border: ${'1px solid #DBDFE9'};
`;

// Default time period for plan summary API calls
const timePeriod = 'full_year';

const firstLastColumnWidth = calculateFontSize(window.innerWidth, 50);
const planSummaryTableId = 'plan-summary-kpi-table';

type Props = {
    applyFilterObject: any; // Current applied filter state from parent
    availableFilters: any; // Available filter dimensions from parent
    setKpiVariable: Function;
    kpiVariable: any;
};

const PlanSummaryContainer: React.FC<Props> = ({
    applyFilterObject,
    availableFilters,
    setKpiVariable,
    kpiVariable,
}) => {
    // Context values
    const {
        planSummarySubTab,
        setPlanSummarySubTab,
        selectedPlanInfo,
        dimensionConfigData,
        eventViewType,
        isKpiAbsView,
        isTpmTab,
        showOptimizedPlanCalender,
        optimizedPlanId,
        planEditMetaData,
        kpiRawData,
    } = useContext(TPODataContext);
    const { appConfig } = useContext(UserContext);

    // Config values
    const tpoConfig = getConfig('tradePromoOptimization', appConfig);
    const enableEditMode = tpoConfig?.enableEditMode;
    const kpiViewOptions = tpoConfig?.kpiViewOptions;
    const commonConfig = getConfig('commonConfig', appConfig);
    const limit = tpoConfig?.tableLimit;
    const statuses = tpoConfig?.statuses;
    const { psProductViewGroupByOptions, psTacticViewGroupByOptions, topPlanSummaryGroupByOptions } = tpoConfig;

    // Active sub-tab
    const activeTab = summaryTabs.find((tab) => tab.key === planSummarySubTab) || summaryTabs[0];

    // Returns the raw group-by config options for the given sub-tab
    const getGroupByOptionsForTab = (tab: PlanSummarySubTab) => {
        return tab === 'ps_tactic_view' ? psTacticViewGroupByOptions : psProductViewGroupByOptions;
    };

    // Returns just the group-by values (e.g. ['material_group', 'brand']) for the given sub-tab
    const getGroupByForTab = (tab: PlanSummarySubTab) => {
        return getGroupByOptionsForTab(tab)?.map((item: any) => item?.value) ?? [];
    };

    // Normalizes group-by config options to include tableHeader/dataKey/sortKey
    // needed by useFetchKpiTableData's groupByMapping builder
    const getDimensionDropdownListForTab = (tab: PlanSummarySubTab) => {
        return (getGroupByOptionsForTab(tab) ?? []).map((item: any) => ({
            ...item,
            tableHeader: item?.tableHeader ?? item?.label,
            dataKey: item?.dataKey ?? item?.value,
            sortKey: item?.sortKey ?? item?.value,
        }));
    };

    const topGroupBy = useMemo(
        () => (topPlanSummaryGroupByOptions ?? []).map((item: any) => item?.value),
        [topPlanSummaryGroupByOptions],
    );
    const topDimensionDropdownList = useMemo(
        () =>
            (topPlanSummaryGroupByOptions ?? []).map((item: any) => ({
                ...item,
                tableHeader: item?.tableHeader ?? item?.label,
                dataKey: item?.dataKey ?? item?.value,
                sortKey: item?.sortKey ?? item?.value,
            })),
        [topPlanSummaryGroupByOptions],
    );

    // Resolve plan_id based on TPM/optimized plan state (same logic as EventsTableContainer)
    const planId = showOptimizedPlanCalender ? optimizedPlanId || selectedPlanInfo?.planId : selectedPlanInfo?.planId;

    // Single state object for all API parameters.
    // All external changes (plan toggle, filters, kpi list, sub-tab) are synced
    // into this state via useEffects below, ensuring React batches concurrent
    // updates into one setState → one API call.
    const [psKpiVariable, setPsKpiVariable] = useState<any>(() => {
        const filters = transformFiltersV2(_.cloneDeep(applyFilterObject), availableFilters);
        return {
            kpi_list: [],
            group_by: [],
            time_period: timePeriod,
            event_type: 'tpo',
            kpi_view: kpiViewOptions?.[0]?.key,
            current_date: dayjs().unix(),
            view_type: planSummarySubTab === 'ps_tactic_view' ? 'promo' : 'plan',
            plan_id: planId,
            is_tpm_plan: showOptimizedPlanCalender ? false : selectedPlanInfo?.isTpmPlan,
            is_tpm_tab: isTpmTab,
            search_key: '',
            sort_by: '',
            limit,
            offset: 0,
            sorting_order: 'asc',
            filters,
        };
    });

    // Update group_by and view_type when sub-tab switches (product view vs tactic view)
    // tactic_view uses 'promo' view_type, product_view uses 'plan'
    useEffect(() => {
        setPsKpiVariable((prev: any) => ({
            ...prev,
            group_by: getGroupByForTab(planSummarySubTab),
            view_type: planSummarySubTab === 'ps_tactic_view' ? 'promo' : 'plan',
            offset: 0,
            sort_by: '',
            sorting_order: 'asc',
        }));
        const topKpiList = (kpiRawData ?? [])
            .filter((item: any) => item?.available_for?.includes('ps_top_view'))
            .map((item: any) => item?.value);
        setTopPsKpiVariable((prev: any) => ({
            ...prev,
            kpi_list: topKpiList,
            group_by: topGroupBy,
            // view_type: planSummarySubTab === 'ps_tactic_view' ? 'promo' : 'plan',
            offset: 0,
        }));
    }, [planSummarySubTab]);

    // Sync filters when parent filter state changes
    useEffect(() => {
        const filters = transformFiltersV2(_.cloneDeep(applyFilterObject), availableFilters);
        setPsKpiVariable((prev: any) => ({ ...prev, filters, offset: 0 }));
        setTopPsKpiVariable((prev: any) => ({ ...prev, filters }));
    }, [applyFilterObject, availableFilters]);

    // Skip the first run — on mount, kpiVariable still has the previous view's
    // kpi_list (e.g. plan_metrics). We start with kpi_list: [] so enabled stays
    // false until PlanActions rebuilds the list for plan_summary and the prop
    // actually changes, giving us a single API call with correct values.
    const isInitialKpiSync = useRef(true);
    useEffect(() => {
        if (isInitialKpiSync.current) {
            isInitialKpiSync.current = false;
            return;
        }
        setPsKpiVariable((prev: any) => ({ ...prev, kpi_list: kpiVariable?.kpi_list ?? [] }));
    }, [kpiVariable?.kpi_list]);

    // Sync search_key when user searches from PlanActions search box
    useEffect(() => {
        setPsKpiVariable((prev: any) => ({ ...prev, search_key: kpiVariable?.search_key ?? '', offset: 0 }));
    }, [kpiVariable?.search_key]);

    // Sync plan_id and TPM flags when original/optimized toggle or plan changes.
    // All related values are batched into a single setState so the query key
    // changes only once, preventing duplicate API calls.
    useEffect(() => {
        const updates = {
            plan_id: planId,
            is_tpm_plan: showOptimizedPlanCalender ? false : selectedPlanInfo?.isTpmPlan,
            is_tpm_tab: isTpmTab,
        };
        setPsKpiVariable((prev: any) => ({ ...prev, ...updates }));
        setTopPsKpiVariable((prev: any) => ({ ...prev, ...updates }));
    }, [planId, showOptimizedPlanCalender, selectedPlanInfo?.isTpmPlan, isTpmTab]);

    // Fetch KPI table data for plan summary
    const {
        loading: kpiTableDataLoading,
        error: kpiTableDataError,
        data: kpiTableData,
        count: kpiTableDataCount,
    } = useFetchKpiTableData({
        method: 'post',
        queryConfig: {
            enabled: Boolean(
                psKpiVariable?.group_by?.length > 0 && psKpiVariable?.kpi_list?.length > 0 && psKpiVariable?.plan_id,
            ),
        },
        url: tradePromoOptimizationUrls.getPlanSummaryTableDataUrl,
        // planSummarySubTab is intentionally excluded — it's already captured
        // by psKpiVariable.group_by, avoiding a stale intermediate API call
        queryKey: ['tpo-event-data', 'tpo-plan-summary-view', psKpiVariable],
        variables: { ...psKpiVariable },
        extras: {
            dimensionDropdownList: getDimensionDropdownListForTab(planSummarySubTab),
            eventViewType,
        },
    });

    const { kpiColumns, nonKpiColumns, result: kpiData } = kpiTableData ?? {};

    // Top plan summary table — uses topPlanSummaryGroupByOptions, same for both views
    const [topPsKpiVariable, setTopPsKpiVariable] = useState<any>(() => {
        const filters = transformFiltersV2(_.cloneDeep(applyFilterObject), availableFilters);
        return {
            kpi_list: [],
            group_by: [],
            time_period: timePeriod,
            event_type: 'tpo',
            kpi_view: kpiViewOptions?.[0]?.key,
            current_date: dayjs().unix(),
            view_type: 'plan',
            plan_id: planId,
            is_tpm_plan: showOptimizedPlanCalender ? false : selectedPlanInfo?.isTpmPlan,
            is_tpm_tab: isTpmTab,
            search_key: '',
            sort_by: '',
            limit: tpoConfig?.tableLimit,
            offset: 0,
            sorting_order: 'asc',
            filters,
        };
    });

    const {
        loading: topTableLoading,
        error: topTableError,
        data: topTableData,
    } = useFetchKpiTableData({
        method: 'post',
        queryConfig: {
            enabled: Boolean(
                topGroupBy?.length > 0 && topPsKpiVariable?.kpi_list?.length > 0 && topPsKpiVariable?.plan_id,
            ),
        },
        url: tradePromoOptimizationUrls.getPlanSummaryTableDataUrl,
        queryKey: ['tpo-event-data', 'tpo-top-plan-summary-view', topPsKpiVariable],
        variables: { ...topPsKpiVariable },
        extras: {
            dimensionDropdownList: topDimensionDropdownList,
            eventViewType,
            from: 'top',
        },
    });

    const { kpiColumns: topKpiColumns, nonKpiColumns: topNonKpiColumns, result: topKpiData } = topTableData ?? {};

    // Auto-calculate table height to fill available screen space
    const tableHeight = useRef<number | null>(0);
    tableHeight.current = useFillHeightToFitScreen({
        id: planSummaryTableId,
        height: tableHeight.current,
        buffer: 254,
    });
    const memoisedTopTableColumns = useMemo(() => {
        if (topKpiData?.length > 0) {
            return getPlanSummaryTableColumns(topKpiColumns, topNonKpiColumns, 220, commonConfig, isKpiAbsView, 'top');
        }
    }, [topKpiData, isKpiAbsView, kpiViewOptions]);

    // Generate table column definitions from KPI data
    const memoisedKpiTableColumns = useMemo(() => {
        if (kpiData?.length > 0) {
            return getPlanSummaryTableColumns(kpiColumns, nonKpiColumns, 180, commonConfig, isKpiAbsView, 'bottom', {
                sortBy: psKpiVariable?.sort_by || 0,
                sortOrder:
                    psKpiVariable?.sorting_order === 'asc'
                        ? 'ascend'
                        : psKpiVariable?.sorting_order === 'desc'
                        ? 'descend'
                        : null,
            });
        }
    }, [kpiData, isKpiAbsView, kpiViewOptions]);

    const topTableExcelData = useMemo(() => {
        if (!topKpiData?.length) return { headers: [], data: [] };
        return planSummaryTableDownload(topKpiColumns, topNonKpiColumns, topKpiData);
    }, [topKpiData, topKpiColumns, topNonKpiColumns]);

    const bottomTableExcelData = useMemo(() => {
        if (!kpiData?.length) return { headers: [], data: [] };
        return planSummaryTableDownload(kpiColumns, nonKpiColumns, kpiData);
    }, [kpiData, kpiColumns, nonKpiColumns]);

    // Infinite scroll — load next page
    function infiniteScrollOnNext() {
        if (kpiTableDataLoading) return;
        setPsKpiVariable({ ...psKpiVariable, offset: psKpiVariable.offset + psKpiVariable.limit });
    }

    // --- Footer text (mirrors EventsTableContainer pattern) ---
    const isErrorStatus = errorStatuses.includes(selectedPlanInfo?.status || '');
    const inProgressStatus = pollingStatuses.includes(selectedPlanInfo?.status || '');
    const isActive = selectedPlanInfo?.status === 'ACTIVE';

    // Record count: "Showing X of Y records"
    const rawCount = isNaN(Number(kpiTableDataCount)) ? 0 : kpiTableDataCount;
    const footerFirstText = `Showing ${Number(kpiData?.length ?? 0)?.toLocaleString()} of ${formatNumberWithCommas(
        rawCount,
        0,
    )} records`;

    // Show editing user name if another user is currently editing the plan
    const footerSecondText =
        enableEditMode && planEditMetaData?.editingUser && !Boolean(compareUsers(planEditMetaData?.editingUser))
            ? planEditMetaData?.editingUser + ' is currently editing'
            : '';

    const footerThirdText = selectedPlanInfo?.createdBy ? `Created by: ${selectedPlanInfo?.createdBy}` : '';
    const footerFourthText = selectedPlanInfo?.updatedAt ? `Last Updated: ${selectedPlanInfo?.updatedAt}` : '';
    return (
        <div className="flex flex-col h-full">
            {/* Sub-tab toggle: Product View / Tactic View */}
            <TileButton
                disabled={kpiTableDataLoading || topTableLoading}
                style={{ width: 'min-content' }}
                data={summaryTabs}
                activeKey={activeTab.key}
                onClick={(item: tileDataProp) => {
                    // setKpiVariable({ ...kpiVariable, group_by: [], kpi_list: [] });
                    setPlanSummarySubTab(item.key as PlanSummarySubTab);
                    setPsKpiVariable({ ...psKpiVariable, kpi_list: [] });
                }}
            />

            {/* Top Plan Summary Table */}
            {topKpiData?.length > 0 && (
                <div className="flex justify-between items-center mt-4 mb-1">
                    <p className="font-semibold text-sm">Plan Summary</p>
                    <DownloadExcel
                        excludeFilters
                        title="PlanSummary"
                        excelData={topTableExcelData.data}
                        headers={topTableExcelData.headers}
                    />
                </div>
            )}
            <div className="border border-backgroundGreyAlt1">
                <CustomTable
                    loading={topTableLoading}
                    bordered
                    columns={memoisedTopTableColumns}
                    scroll={{ x: '100%', y: 86 }}
                    dataSource={topKpiData}
                    pagination={false}
                    style={{
                        pointerEvents: topTableLoading ? 'none' : 'auto',
                    }}
                    locale={{
                        emptyText: topTableLoading ? <></> : topTableError ? '' : '',
                    }}
                />
            </div>

            {/* KPI Table */}
            {kpiData?.length > 0 && (
                <div className="flex justify-between items-center mt-4 mb-1">
                    <p className="font-semibold text-sm">
                        {activeTab.key === 'ps_product_view' ? 'Product Summary' : 'Tactic Summary'}
                    </p>
                    <DownloadExcel
                        excludeFilters
                        title={activeTab.key === 'ps_product_view' ? 'ProductSummary' : 'TacticSummary'}
                        excelData={bottomTableExcelData.data}
                        headers={bottomTableExcelData.headers}
                    />
                </div>
            )}
            <div className="flex-1">
                <InfiniteScroller
                    data={kpiData}
                    loading={kpiTableDataLoading}
                    hasMore={kpiData?.length < (kpiTableDataCount || 0) || kpiTableDataLoading}
                    height={
                        kpiData?.length && !kpiTableDataError
                            ? tableHeight.current ?? undefined
                            : isNaN(Number(tableHeight.current))
                            ? undefined
                            : Number(tableHeight.current) - 1
                    }
                    onNext={() => infiniteScrollOnNext()}
                >
                    <CustomTable
                        id={planSummaryTableId}
                        bordered
                        onChange={(_pagination: any, _filter: any, sort: any) => {
                            if (sort?.order) {
                                const sortMapping: any = { descend: 'desc', ascend: 'asc' };
                                setPsKpiVariable({
                                    ...psKpiVariable,
                                    sort_by: sort?.column?.sortKey,
                                    sorting_order: sortMapping?.[sort?.order] || 'asc',
                                    offset: 0,
                                });
                            }
                        }}
                        sticky={{
                            getContainer: () =>
                                document.getElementsByClassName('infinite-scroll-component')?.[0] as HTMLElement,
                        }}
                        columns={memoisedKpiTableColumns}
                        scroll={{ x: '100%' }}
                        dataSource={kpiData}
                        pagination={false}
                        style={{
                            opacity: kpiTableDataLoading ? '0.3' : 1,
                            pointerEvents: kpiTableDataLoading ? 'none' : 'auto',
                        }}
                        locale={{
                            emptyText: kpiTableDataLoading ? (
                                <></>
                            ) : kpiTableDataError ? (
                                <TableErrorBox error={kpiTableDataError} height={tableHeight.current} />
                            ) : (
                                ''
                            ),
                        }}
                    />
                </InfiniteScroller>
            </div>

            {/* Footer with record count and plan metadata */}
            <div className="relative top-[8px] left-[-16px] w-[calc(100%+32px)]">
                <Footer
                    type={isErrorStatus ? 'error' : inProgressStatus ? 'inProgress' : isActive ? 'success' : 'default'}
                    status={inProgressStatus ? null : statuses?.[selectedPlanInfo?.status || '']?.label}
                    items={[footerFirstText, footerSecondText, footerThirdText, footerFourthText]}
                />
            </div>
        </div>
    );
};

export default React.memo(PlanSummaryContainer);
